import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { blockedReasonCopy, consentNotices, type ConsentTenant } from "../lib/userConnections.js";

// ADR-0152 §4.1: the device-code verification page — the second consent
// surface beside /connect (same between-apps posture: no app shell, no nav,
// session-authenticated with an immediate /login?redirect= bounce). A person
// ran a command in a terminal, got a short code, and typed it here; after
// approval there is nowhere to redirect, so the page just says to return to
// the terminal. Copy carries no "OAuth"/token/RFC/ADR terms.

type DeviceConsent = {
  client_name: string;
  user_code: string;
  created_at: string;
  expires_at: string;
  account_display_name: string;
  tenants: ConsentTenant[];
  /** True when this account may connect hub-global Platform authority (hub admins only). */
  platform_available: boolean;
  /**
   * True when this request arrived through a workspace's own connector URL.
   * The workspace is then already decided: render it as a static line, never a
   * picker, and never offer Platform.
   */
  workspace_locked: boolean;
};

/** Uppercase, strip non-alphanumerics, insert the hyphen — forgiving of how a human types. */
export function formatUserCodeInput(raw: string): string {
  const stripped = raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  return stripped.length > 4 ? `${stripped.slice(0, 4)}-${stripped.slice(4)}` : stripped;
}

/** The workspace select's sentinel for the Platform option — never a real tenant id. */
const PLATFORM_VALUE = "__platform__";

export function Device() {
  const [searchParams, setSearchParams] = useSearchParams();
  const prefill = formatUserCodeInput(searchParams.get("code") ?? "");
  // `resume=1` marks a lookup the person already asked for (they pressed
  // Continue, hit the login bounce, and came back) — see the lookup() note.
  const resumeRequested = searchParams.get("resume") === "1";
  const [code, setCode] = useState(prefill);
  const [consent, setConsent] = useState<DeviceConsent | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [selected, setSelected] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"approved" | "denied" | null>(null);
  // The distinct expired state (2026-07-31 owner defect): a re-issue is a
  // terminal gesture (the CLI holds the secret half of the pair, so only the
  // command can mint a new code) — the page teaches that and resets cleanly.
  const [expired, setExpired] = useState(false);

  async function lookup(userCode: string) {
    setLooking(true);
    setLookupError(null);
    setExpired(false);
    // 2026-07-31 owner defect: a hand-typed code lived only in React state, so
    // the /login bounce (which preserves only the URL) came back to an empty
    // form and re-asked for the code. Writing code+resume into the URL BEFORE
    // the request means the bounce round-trips it, and `resume` records that
    // the person already pressed Continue — so the return leg picks up where
    // they left off instead of re-asking.
    setSearchParams({ code: userCode, resume: "1" }, { replace: true });
    try {
      const res = await apiFetch<DeviceConsent>(`/api/oauth/device/${encodeURIComponent(userCode)}`);
      setConsent(res);
      const firstAllowed = res.tenants.find((t) => t.connect_allowed);
      if (firstAllowed) setSelected(firstAllowed.tenant_id);
      else if (res.platform_available) setSelected(PLATFORM_VALUE);
    } catch (e) {
      setConsent(null);
      if (e instanceof ApiRequestError && e.code === "request_expired") {
        setExpired(true);
      } else {
        setLookupError(
          e instanceof ApiRequestError && e.message
            ? e.message
            : "That code was not recognized — check it and try again."
        );
      }
    } finally {
      setLooking(false);
    }
  }

  // A verification_uri_complete arrival pre-fills the code but never submits —
  // the person still confirms (§4.1). Confirming is one click, not zero. The
  // ONE exception is the post-login return of a lookup the person already
  // asked for (`resume=1`): re-running it is honoring their gesture, not
  // skipping it — approval itself still takes a click either way.
  useEffect(() => {
    if (resumeRequested && prefill.length === 9) void lookup(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startOver() {
    setExpired(false);
    setConsent(null);
    setCode("");
    setLookupError(null);
    setActionError(null);
    setSearchParams({}, { replace: true });
  }

  async function decide(action: "approve" | "deny") {
    if (!consent) return;
    setBusy(true);
    setActionError(null);
    try {
      const body: Record<string, string> = { action };
      if (action === "approve") {
        if (selected === PLATFORM_VALUE) body.context = "platform";
        else body.tenant_id = selected;
      }
      const res = await apiFetch<{ approved: boolean }>(
        `/api/oauth/device/${encodeURIComponent(consent.user_code)}/decision`,
        { method: "POST", body }
      );
      setOutcome(res.approved ? "approved" : "denied");
    } catch (e) {
      // The code can also expire between lookup and decision (the 15-minute
      // TTL keeps ticking through a slow consent) — same teaching state.
      if (e instanceof ApiRequestError && e.code === "request_expired") {
        setConsent(null);
        setExpired(true);
      } else {
        setActionError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  if (outcome) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>
            {outcome === "approved" ? "You're connected." : "Request denied."}
          </div>
          <div className="muted">
            {outcome === "approved"
              ? "Return to your terminal — it will pick up the connection within a few seconds."
              : "The device was not connected. You can close this page."}
          </div>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>That code has expired.</div>
          <div className="muted">
            Codes last 15 minutes. Run the connect command in your terminal again — it will show a
            fresh code — then enter the new code here.
          </div>
          <div className="actions">
            <button className="btn btnPrimary" type="button" onClick={startOver}>
              Enter a new code
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!consent) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull" style={{ display: "grid", gap: 14 }}>
          <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em" }}>Connect a device</div>
          <div className="muted" style={{ fontSize: 14 }}>
            Enter the code shown in your terminal.
          </div>
          <label className="field" style={{ marginBottom: 0 }}>
            <span className="label">Code</span>
            <input
              className="input mono"
              value={code}
              maxLength={9}
              placeholder="XXXX-XXXX"
              autoFocus
              disabled={looking}
              onChange={(e) => setCode(formatUserCodeInput(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && code.length === 9) void lookup(code);
              }}
            />
          </label>
          {lookupError ? <div className="help" style={{ color: "var(--danger-text)" }}>{lookupError}</div> : null}
          <div className="actions">
            <button
              className="btn btnPrimary"
              type="button"
              disabled={looking || code.length !== 9}
              onClick={() => void lookup(code)}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  const allowed = consent.tenants.filter((t) => t.connect_allowed);
  const canApprove = allowed.length > 0 || consent.platform_available;
  const selectedTenant = allowed.find((t) => t.tenant_id === selected) ?? null;

  return (
    <div className="centerPage">
      <div className="card pageNarrow wFull" style={{ display: "grid", gap: 14 }}>
        <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em" }}>Connect to Atlas</div>

        <div className="muted" style={{ fontSize: 14 }}>
          <b style={{ color: "var(--text)" }}>{consent.client_name}</b>{" "}
          <span className="muted" style={{ fontSize: 12 }}>(as reported by the connecting app)</span> wants to access
          Atlas as you.
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <span className="label">Code</span>
          <div className="mono" style={{ fontWeight: 600 }}>{consent.user_code}</div>
        </div>

        {canApprove ? (
          <>
            {!consent.workspace_locked && allowed.length + (consent.platform_available ? 1 : 0) > 1 ? (
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="label">Connect to</span>
                <select className="select" value={selected} disabled={busy} onChange={(e) => setSelected(e.target.value)}>
                  {allowed.map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>
                  ))}
                  {consent.platform_available ? (
                    <option value={PLATFORM_VALUE}>Platform — hub administration</option>
                  ) : null}
                </select>
              </label>
            ) : (
              <div className="field" style={{ marginBottom: 0 }}>
                <span className="label">Connect to</span>
                <div style={{ fontWeight: 600 }}>
                  {selected === PLATFORM_VALUE ? "Platform — hub administration" : selectedTenant?.tenant_name}
                </div>
              </div>
            )}

            <div className="help">
              You'll connect as <b>{consent.account_display_name}</b>
              {selected === PLATFORM_VALUE ? (
                <> with hub administrator access.</>
              ) : selectedTenant?.role_name ? (
                <> with the <b>{selectedTenant.role_name}</b> role.</>
              ) : (
                "."
              )}
            </div>

            {/* The same disclosures as the browser consent screen, in the same
                order (ADR-0206; this screen rendered none before). The platform
                choice is not a workspace, so it carries none. */}
            {consentNotices(selectedTenant).map((notice) => (
              <div key={notice} className="help">{notice}</div>
            ))}

            {actionError ? <div className="help" style={{ color: "var(--danger-text)" }}>{actionError}</div> : null}

            <div className="actions">
              <button className="btn" type="button" disabled={busy} onClick={() => void decide("deny")}>Deny</button>
              <button className="btn btnPrimary" type="button" disabled={busy || !selected} onClick={() => void decide("approve")}>Approve</button>
            </div>
          </>
        ) : (
          <>
            <div className="muted">
              {consent.tenants.length === 0
                ? consent.workspace_locked
                  // Identical for "no such workspace" and "you're not in it".
                  ? "You don't have a connection role in this workspace yet. Ask a workspace admin to assign you one."
                  : "You don't have a connection role in any workspace yet. Ask a workspace admin to assign you one in Users & Teams."
                : blockedReasonCopy(consent.tenants[0].blocked_reason)}
            </div>
            {actionError ? <div className="help" style={{ color: "var(--danger-text)" }}>{actionError}</div> : null}
            <div className="actions">
              <button className="btn" type="button" disabled={busy} onClick={() => void decide("deny")}>Deny</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
